import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Users, Building2, GraduationCap, AlertTriangle, 
  CheckCircle2, Clock, AlertCircle, TrendingUp, Network
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const [resumen, setResumen] = useState(null);
  const [cumplimientoAreas, setCumplimientoAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resumenRes, areasRes] = await Promise.all([
        axios.get(`${API}/cumplimiento/resumen`),
        axios.get(`${API}/cumplimiento/por-area`)
      ]);
      setResumen(resumenRes.data);
      setCumplimientoAreas(areasRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const stats = [
    { 
      title: 'Total Personas', 
      value: resumen?.total_personas || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      link: '/personas'
    },
    { 
      title: 'Capacitaciones', 
      value: resumen?.total_capacitaciones || 0, 
      icon: GraduationCap, 
      color: 'bg-emerald-500',
      link: '/capacitaciones'
    },
    { 
      title: 'Cumplidas', 
      value: resumen?.cumplidas || 0, 
      icon: CheckCircle2, 
      color: 'bg-green-500',
      link: '/reportes'
    },
    { 
      title: 'Por Vencer (30d)', 
      value: resumen?.por_vencer_30_dias || 0, 
      icon: Clock, 
      color: 'bg-amber-500',
      link: '/reportes'
    },
    { 
      title: 'Vencidas', 
      value: resumen?.vencidas || 0, 
      icon: AlertCircle, 
      color: 'bg-red-500',
      link: '/reportes'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white" data-testid="dashboard-header">
        <h1 className="text-2xl font-bold tracking-tight mb-1">Panel de Control</h1>
        <p className="text-teal-100 text-sm">Monitorea el cumplimiento organizacional en tiempo real</p>
      </div>

      {/* Main KPI */}
      <Card className="border border-slate-200 shadow-sm" data-testid="cumplimiento-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Cumplimiento General</h3>
              <p className="text-sm text-slate-500">Porcentaje de capacitaciones completadas</p>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-500" />
              <span className="text-3xl font-bold text-teal-500">
                {resumen?.porcentaje_cumplimiento || 0}%
              </span>
            </div>
          </div>
          <Progress 
            value={resumen?.porcentaje_cumplimiento || 0} 
            className="h-3 bg-slate-100"
          />
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card 
              className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.title}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Compliance by Area */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-500" />
            Cumplimiento por Área
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cumplimientoAreas.length > 0 ? (
            <div className="space-y-4">
              {cumplimientoAreas.map((area) => (
                <div key={area.area_id} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{area.area_nombre}</span>
                    <span className="text-slate-500">
                      {area.cumplidas}/{area.total} ({area.porcentaje}%)
                    </span>
                  </div>
                  <Progress value={area.porcentaje} className="h-2 bg-slate-100" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">
              No hay datos de cumplimiento disponibles
            </p>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { name: 'Organigrama', icon: Network, href: '/organigrama', color: 'text-purple-500' },
          { name: 'Gestionar Áreas', icon: Building2, href: '/areas', color: 'text-blue-500' },
          { name: 'Gestionar Riesgos', icon: AlertTriangle, href: '/riesgos', color: 'text-amber-500' },
          { name: 'Ver Reportes', icon: CheckCircle2, href: '/reportes', color: 'text-green-500' },
        ].map((link) => (
          <Link key={link.name} to={link.href}>
            <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all hover:border-teal-200 cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <link.icon className={`w-5 h-5 ${link.color}`} />
                <span className="font-medium text-slate-700">{link.name}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};
